import groupBy from 'lodash/groupBy'
import { useCallback, useMemo } from 'react'
import { useConfig } from '../providers/config'
import { useStore } from '../providers/store'
import {
	formatStoreCookieName,
	isMatchStoreByCodeInPath,
	isMatchStoreByDevUrl,
	isMatchStoreByNone,
	isMatchStoreByUrl,
} from '../utils'

const PATH_ROOT = '/'

export const useCookiesByStoreActions = ({ setCookie, removeCookie }) => {
	const { availableStores } = useConfig()
	const { groupId: currentGroupId, hostname: currentHostname } = useStore()

	const storesOfCurrentGroup = useMemo(
		() => groupBy(availableStores, 'groupId')[currentGroupId] || [],
		[availableStores, currentGroupId]
	)

	const storesOfCurrentGroupAndCurrentHostname = useMemo(
		() => storesOfCurrentGroup?.filter(({ hostname }) => hostname === currentHostname),
		[currentHostname, storesOfCurrentGroup]
	)

	const saveOnAllStoreOfCurrentGroup = useCallback(
		(name, value, options) => {
			for (const storeOfCurrentGroupAndCurrentUrl of storesOfCurrentGroupAndCurrentHostname) {
				const { code, hostname: domain } = storeOfCurrentGroupAndCurrentUrl
				setCookie(formatStoreCookieName(code, name), value, {
					path: PATH_ROOT,
					domain,
					...options,
				})
			}
		},
		[storesOfCurrentGroupAndCurrentHostname, setCookie]
	)

	const removeOnAllStoreOfCurrentGroup = useCallback(
		(name, options) => {
			for (const storeOfCurrentGroupAndCurrentUrl of storesOfCurrentGroupAndCurrentHostname) {
				const { code, hostname: domain } = storeOfCurrentGroupAndCurrentUrl
				removeCookie(formatStoreCookieName(code, name), {
					path: PATH_ROOT,
					domain,
					...options,
				})
			}
		},
		[storesOfCurrentGroupAndCurrentHostname, removeCookie]
	)

	const saveOnAllStoresByPath = useCallback(
		(name, value, options) => {
			for (const store of storesOfCurrentGroup) {
				const { code } = store
				setCookie(formatStoreCookieName(code, name), value, {
					path: PATH_ROOT,
					...options,
				})
			}
		},
		[storesOfCurrentGroup, setCookie]
	)
	const removeFromAllStoresByPath = useCallback(
		(name, options) => {
			for (const store of storesOfCurrentGroup) {
				const { code } = store
				removeCookie(formatStoreCookieName(code, name), {
					path: PATH_ROOT,
					...options,
				})
			}
		},
		[storesOfCurrentGroup, removeCookie]
	)

	const setCookiesByStore = useCallback(
		(name, value, options) => {
			if (isMatchStoreByUrl() || isMatchStoreByDevUrl()) {
				saveOnAllStoreOfCurrentGroup(name, value, options)
			} else if (isMatchStoreByCodeInPath()) {
				saveOnAllStoresByPath(name, value, options)
			} else if (isMatchStoreByNone()) {
				setCookie(name, value, options)
			}
		},
		[saveOnAllStoreOfCurrentGroup, saveOnAllStoresByPath, setCookie]
	)

	const removeCookiesByStore = useCallback(
		(name, options) => {
			if (isMatchStoreByUrl() || isMatchStoreByDevUrl()) {
				removeOnAllStoreOfCurrentGroup(name, options)
			} else if (isMatchStoreByCodeInPath()) {
				removeFromAllStoresByPath(name, options)
			} else if (isMatchStoreByNone()) {
				removeCookie(name, options)
			}
		},
		[removeOnAllStoreOfCurrentGroup, removeFromAllStoresByPath, removeCookie]
	)

	return { setCookiesByStore, removeCookiesByStore }
}
